/**
 * Les prestataires : coiffeuses, prothésistes ongulaires, barbiers.
 *
 * Le pourcentage est LA donnée sensible de cette page — c'est sur lui que le
 * propriétaire paie. Il est figé sur chaque ligne de ticket au moment de la
 * vente : le modifier ici ne change rien aux ventes déjà encaissées.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Typography, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Chip, Switch, FormControlLabel, CircularProgress,
  Alert, Tooltip, Card, CardContent, InputAdornment, Avatar,
} from '@mui/material';
import {
  Edit, Delete, Add, Search, PersonOff, PersonAdd, Groups,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { prestataireAPI, extraireListe } from '../../../services/studioAPI';

const SPECIALITES = [
  { value: 'ongles', label: 'Ongles' },
  { value: 'coiffure', label: 'Coiffure' },
  { value: 'perruque', label: 'Perruque' },
  { value: 'barbier', label: 'Barbier / Coupe' },
  { value: 'polyvalent', label: 'Polyvalent' },
  { value: 'autre', label: 'Autre' },
];

const FORMULAIRE_VIDE = {
  nom: '', prenom: '', telephone: '', specialite: 'polyvalent',
  pourcentage: '', date_embauche: '', notes: '', est_actif: true,
};

const Prestataires = () => {
  const [prestataires, setPrestataires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [dialogOuvert, setDialogOuvert] = useState(false);
  const [enEdition, setEnEdition] = useState(false);
  const [formulaire, setFormulaire] = useState(FORMULAIRE_VIDE);

  const charger = async () => {
    try {
      setLoading(true);
      setErreur(null);
      const reponse = await prestataireAPI.getAll();
      setPrestataires(extraireListe(reponse));
    } catch (error) {
      const message = error.response?.data?.detail || error.message
        || 'Erreur lors du chargement des prestataires';
      setErreur(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { charger(); }, []);

  const affiches = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    if (!terme) return prestataires;
    return prestataires.filter((p) =>
      `${p.nom_complet} ${p.telephone || ''} ${p.specialite_display || ''}`
        .toLowerCase().includes(terme)
    );
  }, [prestataires, recherche]);

  const actifs = prestataires.filter((p) => p.est_actif);
  const pourcentageMoyen = actifs.length
    ? Math.round(actifs.reduce((s, p) => s + Number(p.pourcentage || 0), 0) / actifs.length)
    : 0;

  const ouvrirDialog = (prestataire = null) => {
    if (prestataire) {
      setFormulaire({
        id: prestataire.id,
        nom: prestataire.nom,
        prenom: prestataire.prenom || '',
        telephone: prestataire.telephone || '',
        specialite: prestataire.specialite,
        pourcentage: prestataire.pourcentage,
        date_embauche: prestataire.date_embauche || '',
        notes: prestataire.notes || '',
        est_actif: prestataire.est_actif,
      });
      setEnEdition(true);
    } else {
      setFormulaire(FORMULAIRE_VIDE);
      setEnEdition(false);
    }
    setDialogOuvert(true);
  };

  const enregistrer = async () => {
    if (!formulaire.nom.trim()) return toast.error('Le nom est obligatoire');
    const pourcentage = Number(formulaire.pourcentage);
    if (formulaire.pourcentage === '' || pourcentage < 0 || pourcentage > 100) {
      return toast.error('Le pourcentage doit être compris entre 0 et 100');
    }

    try {
      const donnees = {
        ...formulaire,
        pourcentage,
        // Chaîne vide → null : DRF refuse '' sur un DateField
        date_embauche: formulaire.date_embauche || null,
      };
      if (enEdition) {
        await prestataireAPI.update(formulaire.id, donnees);
        toast.success('Prestataire modifié');
      } else {
        await prestataireAPI.create(donnees);
        toast.success('Prestataire ajouté');
      }
      setDialogOuvert(false);
      charger();
    } catch (error) {
      toast.error(
        error.response?.data?.nom?.[0] || error.response?.data?.pourcentage?.[0]
        || error.response?.data?.detail || "Erreur lors de l'enregistrement"
      );
    }
  };

  const basculerStatut = async (prestataire) => {
    try {
      await prestataireAPI.patch(prestataire.id, { est_actif: !prestataire.est_actif });
      toast.success(prestataire.est_actif ? 'Prestataire désactivé' : 'Prestataire réactivé');
      charger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de statut');
    }
  };

  const supprimer = async (prestataire) => {
    if (!window.confirm(`Supprimer ${prestataire.nom_complet} ?`)) return;
    try {
      await prestataireAPI.delete(prestataire.id);
      toast.success('Prestataire supprimé');
      charger();
    } catch (error) {
      // PROTECT : ses lignes de ticket doivent rester traçables
      toast.error(
        'Impossible de supprimer quelqu\'un qui a déjà réalisé des prestations — désactivez-le plutôt'
      );
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="1.5rem 2.5rem">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Prestataires</Typography>
          <Typography variant="body2" color="text.secondary">
            Qui travaille au studio, et à quel pourcentage
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => ouvrirDialog()}>
          Nouveau prestataire
        </Button>
      </Box>

      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">En activité</Typography>
            <Typography variant="h4" fontWeight="bold" color="success.main">
              {actifs.length}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Fiches au total</Typography>
            <Typography variant="h4" fontWeight="bold">{prestataires.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Pourcentage moyen</Typography>
            <Typography variant="h4" fontWeight="bold">{pourcentageMoyen} %</Typography>
          </CardContent>
        </Card>
      </Box>

      <Box mb={2}>
        <TextField
          size="small"
          placeholder="Chercher un nom, un téléphone, une spécialité"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          sx={{ maxWidth: 380 }}
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Prestataire</TableCell>
              <TableCell>Spécialité</TableCell>
              <TableCell>Téléphone</TableCell>
              <TableCell align="center">Pourcentage</TableCell>
              <TableCell align="center">Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {affiches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Groups sx={{ fontSize: 48, color: 'text.disabled' }} />
                  <Typography color="text.secondary" mt={1}>
                    {prestataires.length === 0
                      ? 'Aucun prestataire — la caisse ne pourra pas enregistrer qui fait quoi'
                      : 'Personne ne correspond'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : affiches.map((prestataire) => (
              <TableRow key={prestataire.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.light' }}>
                      {(prestataire.nom_complet || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography fontWeight={600}>{prestataire.nom_complet}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={prestataire.specialite_display} variant="outlined" />
                </TableCell>
                <TableCell>{prestataire.telephone || '—'}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={`${Number(prestataire.pourcentage)} %`}
                    color="primary"
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={prestataire.est_actif ? 'Actif' : 'Inactif'}
                    color={prestataire.est_actif ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={prestataire.est_actif ? 'Désactiver' : 'Réactiver'}>
                    <IconButton size="small" onClick={() => basculerStatut(prestataire)}>
                      {prestataire.est_actif ? <PersonOff /> : <PersonAdd />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Modifier">
                    <IconButton size="small" onClick={() => ouvrirDialog(prestataire)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <IconButton size="small" color="error" onClick={() => supprimer(prestataire)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOuvert} onClose={() => setDialogOuvert(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{enEdition ? 'Modifier le prestataire' : 'Nouveau prestataire'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Box display="flex" gap={2}>
              <TextField
                label="Nom"
                value={formulaire.nom}
                onChange={(e) => setFormulaire({ ...formulaire, nom: e.target.value })}
                fullWidth
                autoFocus
                required
              />
              <TextField
                label="Prénom"
                value={formulaire.prenom}
                onChange={(e) => setFormulaire({ ...formulaire, prenom: e.target.value })}
                fullWidth
              />
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                label="Téléphone"
                value={formulaire.telephone}
                onChange={(e) => setFormulaire({ ...formulaire, telephone: e.target.value })}
                fullWidth
              />
              <TextField
                label="Spécialité"
                select
                value={formulaire.specialite}
                onChange={(e) => setFormulaire({ ...formulaire, specialite: e.target.value })}
                fullWidth
              >
                {SPECIALITES.map((specialite) => (
                  <MenuItem key={specialite.value} value={specialite.value}>
                    {specialite.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                label="Pourcentage"
                type="number"
                value={formulaire.pourcentage}
                onChange={(e) => setFormulaire({ ...formulaire, pourcentage: e.target.value })}
                fullWidth
                required
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                helperText="Sa part sur tout ce qu'il réalise"
              />
              <TextField
                label="Date d'embauche"
                type="date"
                value={formulaire.date_embauche}
                onChange={(e) => setFormulaire({ ...formulaire, date_embauche: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Alert severity="info">
              Le pourcentage est figé sur chaque ligne de ticket au moment de la vente :
              le changer ici n'a aucun effet sur les tickets déjà encaissés.
            </Alert>
            <TextField
              label="Notes"
              value={formulaire.notes}
              onChange={(e) => setFormulaire({ ...formulaire, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formulaire.est_actif}
                  onChange={(e) => setFormulaire({ ...formulaire, est_actif: e.target.checked })}
                />
              }
              label="Proposé à la caisse"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOuvert(false)}>Annuler</Button>
          <Button variant="contained" onClick={enregistrer}>
            {enEdition ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Prestataires;
